import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RainbowText from '../atoms/RainbowText';
import { typography, colors } from '../../utils/styling';
import { QUESTIONS } from '../../utils/quizinfo/questions';
import { estimatedQuizMinutes } from '../../utils/quizinfo/estimate';

// Derived, never hardcoded: the quiz page pins the same estimate (quiz.tsx).
const QUIZ_MINUTES = estimatedQuizMinutes(QUESTIONS.length);

// Homepage hero, redesigned from McKayla's 2026-10-05 design Loom: fill the
// screen, stop centering everything, put something dynamic behind the text
// (she pointed at Nitrogen and Jump), and use the brain-logo "hub" with the
// five personalities around it (her Canva page 10) as a clickable graphic.

const HUB = [
  { name: 'Dream Chaser', slug: 'dreamchaser', color: '#00A4EF' },
  { name: 'Safety Seeker', slug: 'safetyseeker', color: '#076652' },
  { name: 'Freedom Finder', slug: 'freedomfinder', color: '#FF00FF' },
  { name: 'Wealth Architect', slug: 'wealtharchitect', color: '#00B3C2' },
  { name: 'Generous Altruist', slug: 'generousaltruist', color: '#9933FF' },
];

// Nina + McKayla, 2026-10-07 call: the green feature carousel under the hero
// must be visible without scrolling. Desktop: header (90) + hero padding (48)
// + carousel strip (~250) leaves the rest of the screen for the hub, so the
// hub shrinks on short screens instead of pushing the carousel off the fold.
const DESKTOP_HUB_MAX = 520;
const DESKTOP_HUB_MIN = 340;
const RESERVED_BELOW_AND_ABOVE_HUB = 388;

function useViewportHeight() {
  const [vh, setVh] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight));
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return vh;
}

const PersonalityHub = ({ isMobile }: { isMobile: boolean }) => {
  const navigate = useNavigate();
  const vh = useViewportHeight();
  const desktopSize = Math.max(DESKTOP_HUB_MIN, Math.min(DESKTOP_HUB_MAX, vh - RESERVED_BELOW_AND_ABOVE_HUB));
  const size = isMobile ? 280 : desktopSize;
  const bubble = isMobile ? 80 : Math.round(desktopSize * 0.265);
  const radius = (size - bubble) / 2;
  const center = size / 2;
  return (
    <div className="mp-hub" style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ position: 'absolute', inset: 0 }} aria-hidden>
        {HUB.map((p, i) => {
          const a = (-90 + i * 72) * (Math.PI / 180);
          return (
            <line
              key={p.slug}
              x1={center}
              y1={center}
              x2={center + radius * Math.cos(a)}
              y2={center + radius * Math.sin(a)}
              stroke={p.color}
              strokeOpacity={0.35}
              strokeWidth={2}
              strokeDasharray="6 6"
              className="mp-hub-line"
            />
          );
        })}
      </svg>
      <img
        src={require('../img/mp_transparent_logo.png')}
        alt="The Money Personality logo"
        className="mp-hub-logo"
        style={{
          position: 'absolute',
          width: isMobile ? 96 : 150,
          left: center - (isMobile ? 48 : 75),
          top: center - (isMobile ? 48 : 75),
        }}
      />
      {HUB.map((p, i) => {
        const a = (-90 + i * 72) * (Math.PI / 180);
        return (
          <button
            key={p.slug}
            type="button"
            className="mp-hub-bubble"
            onClick={() => navigate(`/personalities/${p.slug}`)}
            style={{
              position: 'absolute',
              width: bubble,
              height: bubble,
              left: center + radius * Math.cos(a) - bubble / 2,
              top: center + radius * Math.sin(a) - bubble / 2,
              backgroundColor: p.color,
              animationDelay: `${i * 0.6}s`,
              fontSize: isMobile ? 12 : bubble >= 120 ? 17 : 14,
            }}
          >
            The {p.name}
          </button>
        );
      })}
    </div>
  );
};

const HeroSection = ({ isMobile, onTakeQuiz }: { isMobile: boolean; onTakeQuiz: () => void }) => (
  <section
    className="mp-hero"
    style={{
      width: '100%',
      marginTop: isMobile ? 80 : 90,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      padding: isMobile ? '32px 20px 28px' : '24px 64px',
    }}
  >
    <div
      style={{
        width: '100%',
        maxWidth: 1320,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 36 : 48,
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div className="mp-hero-copy" style={{ maxWidth: 600, textAlign: isMobile ? 'center' : 'left' }}>
        <div style={{ fontSize: isMobile ? 40 : 68, fontWeight: 300, lineHeight: 1.05, fontFamily: typography.fontFamily.body, color: colors.black }}>
          Discover your
          <br />
          money <RainbowText text="personality" />
        </div>
        <p style={{ fontSize: isMobile ? 17 : 20, lineHeight: 1.6, color: colors.gray[700], margin: isMobile ? '20px 0 28px' : '28px 0 36px', fontFamily: 'Nunito' }}>
          Money Personality helps you understand your relationship with money and
          make better-informed decisions about finances.
        </p>
        <button type="button" className="mp-hero-cta" onClick={onTakeQuiz}>
          Take quiz
        </button>
        <p style={{ marginTop: 16, fontSize: 14, color: colors.gray[600], fontFamily: 'Nunito' }}>
          Free · about {QUIZ_MINUTES} minutes · tap a personality to explore it
        </p>
      </div>
      <PersonalityHub isMobile={isMobile} />
    </div>
  </section>
);

export default HeroSection;
